import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ClientMainNote, ClientMainNoteUpsertRequest } from '@crm/shared';
import { api } from '../../lib/api';
import { clientsKeys } from './api';

/**
 * Klucze cache dla głównej notatki klienta (Faza 18).
 * Każda mutacja inwaliduje:
 *   - własny klucz (force refetch notatki),
 *   - listę i detal klienta (bo zawierają mainNote preview / pełną notatkę),
 *   - wszystkie listy wizyt (kalendarz pokazuje ikonę zależną od mainNote).
 */
export const clientMainNoteKeys = {
  one: (clientId: number) => ['clients', 'mainNote', clientId] as const,
};

export function useClientMainNote(clientId: number | null) {
  return useQuery({
    queryKey:
      clientId !== null
        ? clientMainNoteKeys.one(clientId)
        : ['clients', 'mainNote', 'noop'],
    enabled: clientId !== null,
    queryFn: async () => {
      const res = await api.get<ClientMainNote | null>(
        `/clients/${clientId}/main-note`,
      );
      return res.data;
    },
  });
}

function invalidateAllAffected(qc: ReturnType<typeof useQueryClient>, clientId: number) {
  qc.invalidateQueries({ queryKey: clientMainNoteKeys.one(clientId) });
  qc.invalidateQueries({ queryKey: clientsKeys.one(clientId) });
  qc.invalidateQueries({ queryKey: clientsKeys.all });
  // Kalendarz wizyt — `mainNote` jest w client summary każdej wizyty.
  qc.invalidateQueries({ queryKey: ['appointments'] });
}

export function useClientMainNoteUpsert(clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ClientMainNoteUpsertRequest) => {
      const res = await api.put<ClientMainNote>(
        `/clients/${clientId}/main-note`,
        input,
      );
      return res.data;
    },
    onSuccess: (data) => {
      qc.setQueryData(clientMainNoteKeys.one(clientId), data);
      invalidateAllAffected(qc, clientId);
    },
  });
}

export function useClientMainNoteDelete(clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.delete(`/clients/${clientId}/main-note`);
    },
    onSuccess: () => {
      qc.setQueryData(clientMainNoteKeys.one(clientId), null);
      invalidateAllAffected(qc, clientId);
    },
  });
}
